import { useAppSelector } from 'hooks/reduxHooks'
import { getExperiencesTimesKeyFromCartItem } from 'lib/experiences/experienceUtils'
import { mapExperienceCartItemToSummaryViews } from 'checkout/lib/utils/experiences/view'
import { useMemo, useCallback, useContext } from 'react'
import GeoContext from 'contexts/geoContext'
import { isNonNullable } from 'lib/array/arrayUtils'

type ExperienceTimesResult = Record<string, App.ApiCallState<{
  slots?: Array<{
    time: string
    tickets: Array<App.Checkout.ExperienceItemTicketSummaryView>
  }>
}>>

export function useExperienceTimesForCart(items: Array<App.Checkout.ExperienceItem>): ExperienceTimesResult {
  const experienceTimes = useAppSelector(state => state.experience.experienceTimes)
  const { currentCurrency } = useContext(GeoContext)

  const mapTickets = useCallback((item: App.Checkout.ExperienceItem, slot: App.ExperienceAvailabilityTimeSlot) => ({
    time: slot.time,
    tickets: item.tickets
      .map(cartTicket => {
        const ticket = slot.tickets.find(t => (
          t.id === cartTicket.ticketId &&
          t.date === cartTicket.date
        ))
        return mapExperienceCartItemToSummaryViews(
          cartTicket,
          ticket,
        )
      })
      .filter(isNonNullable),
  }), [])

  return useMemo(() => {
    return items.reduce<ExperienceTimesResult>((acc, item) => {
      const key = getExperiencesTimesKeyFromCartItem(item, currentCurrency)
      const apiCallState = experienceTimes[item.experienceId]?.[key]

      if (!apiCallState) {
        acc[item.itemId] = { fetching: false }
        return acc
      }

      acc[item.itemId] = {
        ...apiCallState,
        slots: apiCallState.slots?.map(slot => mapTickets(item, slot)),
      }
      return acc
    }, {})
  }, [items, experienceTimes, currentCurrency, mapTickets])
}
